import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import produce from 'immer';

import { DatasetActions } from '@mode/dataset/contract';

import { RMap, SwitchViewDomain } from '@mode/shared/contract-common';
import { deepCopy, immRemoveKey } from '@mode/shared/util-js';
import { SwitchViewActions } from '@mode/switch-views/contract';
import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';

import { CellExportActions } from '@mode/notebooks/contract';

export type SwitchViewState = EntityState<SwitchViewDomain.SwitchView>;

export interface SwitchViewFeatureState {
  views: SwitchViewState;
  refIds: RMap<string>;
  viewsLoaded: boolean;
}

export const switchViewFeatureKey = 'switchView';

const adapter: EntityAdapter<SwitchViewDomain.SwitchView> = createEntityAdapter<SwitchViewDomain.SwitchView>({
  selectId: (model) => model.token,
});

export const initialState: SwitchViewFeatureState = {
  views: adapter.getInitialState(),
  refIds: {},
  viewsLoaded: true,
};
export const switchViewAdapter = adapter;

export const refIdsReducer = createReducer(
  initialState.refIds,
  on(SwitchViewActions.setRef, (state, action) => ({
    ...state,
    [action.refId]: action.switchViewToken,
  })),
  on(SwitchViewActions.setRefs, (state, action) => {
    return produce(state, (draft) => {
      action.refs.forEach((ref) => {
        draft[ref.refId] = ref.switchViewToken;
      });
    });
  }),
  on(SwitchViewActions.deleteRef, (state, action) => immRemoveKey(state, action.refId)),
  on(SwitchViewActions.deleteRefs, (state, action) => {
    return produce(state, (draft) => {
      action.refIds.forEach((refId) => {
        delete draft[refId];
      });
    });
  })
);

export const viewsReducer = createReducer(
  initialState.views,

  on(SwitchViewActions.loadSwitchViewSuccess, DatasetActions.loadDatasetSuccess, (state, action) =>
    adapter.setOne(action.switchView, state)
  ),
  on(SwitchViewActions.pollingSwitchViewCompleted, (state, action) =>
    adapter.setOne(action.dataView.switchView, state)
  ),
  on(
    SwitchViewActions.loadGfDataViews,
    SwitchViewActions.loadEditorViewsSuccess,
    SwitchViewActions.refreshEditorViewsSuccess,
    SwitchViewActions.loadReportViewDataViews,
    (state, action) => {
      const switchViews = action.dataViews.map(({ switchView }) => switchView);
      /**
       * @todo(@kevinjamesus86) GF
       * @description We are clearing out the entity store with `setAll` to resolve an
       * ambiguity issue in looking up switch views by queryToken. The following flow
       * exercises the issue:
       *
       * [Load] View Report
       *   ** action:loadGfDataViews
       *   -> Click [Explore] on a viz
       *   -> Save an exploration of the viz
       *   ** action:loadExploration
       *   -> [★★ see below ★★]
       *
       * Problem
       * -------
       *
       * There are now 2 SwitchViews in the store that ref the same queryToken. This presents
       * an issue downstream with `getModel` selectors and the way we look up views in a model
       * by viewName, which is a query token. There will be two views in the model that are keyed by
       * said query token; the first match in the collection is returned by Switch.Views.find(model, viewName).
       * As it is right now, we do not have a way to distinguish between two views in a model or
       * select a _specific_ one in the case more than one matches a query token.
       *
       * [★★] Problem in practice
       * [★★] -------------------
       *
       * After saving the exploration, _without reloading the page_, create a calculated field
       * that's local to the exploration. The new calulation will be added to the correct SwitchView
       * and show up in the field list as expected; but, adding said field to the encoding will result
       * in a broken viz / _invalid encoding_. This is because the encoding builder selects a view from
       * the model by query token, vs. working with the view$ observable passed down from the observables
       * factory in a parent component.. which selects and constructs the correct view [by SwitchView token].
       *
       * Round up
       * --------
       *
       * Reports | Explorations load the data they need when they need it; clearing out the store
       * per action should not adversely affect the other. This works for now 👍
       *
       *             ↓↓↓↓↓↓ setAll replaces the existing collection with the provided collection
       */
      return adapter.setAll(switchViews, state);
    }
  ),

  on(SwitchViewActions.loadExplorationDataView, (state, { dataView }) => {
    return adapter.setOne(
      dataView.switchView,
      /**
       * @todo(@kevinjamesus86) GF
       * @description see above comments for on(loadGfDataViews, ...)
       */
      adapter.removeAll(state)
    );
  }),

  on(SwitchViewActions.loadVisualizationDataView, (state, action) => adapter.setOne(action.dataView.switchView, state)),

  on(SwitchViewActions.loadImportedDatasetSuccess, (state, action) => {
    return adapter.addOne(action.dataView.switchView, state);
  }),

  on(SwitchViewActions.importedDatasetRefreshed, (state, action) => {
    return adapter.setOne(action.switchView, state);
  }),

  // reorder actions
  on(SwitchViewActions.reorderDatasetsSuccess, (state, action) => {
    return adapter.map((switchView) => {
      return {
        ...switchView,
        rowOrder: action.switchViewsRankMap[switchView.token],
      };
    }, state);
  }),
  on(
    SwitchViewActions.reorderDatasetsFailure,
    /*We want to simply return the current state as the editor browser's list was already
     * optimistically updated and we want to revert the state to the original state there
     * in case of a failure
     *
     * Use of deepCopy is to create a new copy of the original state and all its internal
     * objects to retrigger selectors
     */
    (state) => deepCopy(state)
  )
);

export const viewsLoadedReducer = createReducer(
  initialState.viewsLoaded,
  on(
    SwitchViewActions.loadEditorViews,
    DatasetActions.loadDatasetViewRoute,
    DatasetActions.loadDatasetEditRoute,
    CellExportActions.refreshDataViewsAfterExport,
    () => false
  ),
  on(
    SwitchViewActions.loadEditorViewsSuccess,
    SwitchViewActions.refreshEditorViewsSuccess,
    DatasetActions.loadDatasetSuccess,
    SwitchViewActions.pollingSwitchViewCompleted,
    SwitchViewActions.loadReportViewDataViews,
    SwitchViewActions.loadGfDataViews,
    SwitchViewActions.loadExplorationDataView,
    SwitchViewActions.loadVisualizationDataView,
    SwitchViewActions.loadImportedDatasetSuccess,
    SwitchViewActions.importedDatasetRefreshed,
    () => true
  )
);

const { selectIds, selectEntities, selectAll, selectTotal } = adapter.getSelectors();

const reducer: ActionReducerMap<SwitchViewFeatureState> = {
  views: viewsReducer,
  refIds: refIdsReducer,
  viewsLoaded: viewsLoadedReducer,
};

export { adapter, reducer };

export const selectViewIds = selectIds;
export const selectViewEntities = selectEntities;
export const selectAllViews = selectAll;
export const selectViewTotal = selectTotal;
